import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";

import { useTranslation } from "react-i18next";

import operatorCable from "@/lib/operatorCable";
import { operatorApiFetch } from "@/services/operator/apiService";
import { matchProductInFulfillments } from "@/utils/distributeMatching";

/**
 * DistributeContext - 配貨作業狀態管理（基於 PickingWave）
 *
 * 資料格式設計：
 *
 * distributeState = [
 *   {
 *     slotNumber: 1,                    // Grid 位置（1-based，來自 fulfillment.slot_number）
 *     order: {
 *       id: 123,
 *       name: "ORD-001",
 *       receiver_name: "John Doe",
 *     },
 *     distributed_items: [                // 已分配的商品項目
 *       {
 *         order_item_id: 456,             // OrderItem ID
 *         product_id: 789,
 *         sku: "SKU-001",
 *         product_name: "Product A",
 *         batch: "BATCH-001",
 *         barcode: "1234567890",
 *         required_quantity: 10,
 *         distributed_quantity: 5,
 *         last_distributed_at: "2025-01-01T12:00:00Z",
 *         last_distributed_by: { id: 1, name: "Operator A" }
 *       }
 *     ]
 *   }
 * ]
 *
 * 入口掃碼邏輯：
 * 1. 掃描 PW-xxx → 直接載入該 wave
 * 2. 掃描 FL-xxx → 透過 fulfillment 反查所屬 wave → 載入
 * 3. 掃描 UUID → 嘗試作為 wave ID 查找
 * ※ 不支援 PL-xxx（揀貨單無法精確對應 fulfillments 的 slot 分配）
 */

const DistributeContext = createContext(null);

export const useDistribute = () => {
  const context = useContext(DistributeContext);
  if (!context) {
    throw new Error("useDistribute must be used within DistributeProvider");
  }
  return context;
};

// ==================== 輔助函數 ====================

/**
 * 檢查這次掃描是否會讓整個訂單完成
 */
function checkWillBeComplete(state, slotNumber, orderItemId) {
  const cell = state.find((c) => c.slotNumber === slotNumber);
  if (!cell) return false;

  const wasComplete = cell.distributed_items.every(
    (i) => i.distributed_quantity >= i.required_quantity,
  );
  if (wasComplete) return false;

  const willComplete = cell.distributed_items.every((i) => {
    if (i.order_item_id === orderItemId) {
      return i.distributed_quantity + 1 >= i.required_quantity;
    }
    return i.distributed_quantity >= i.required_quantity;
  });

  return !wasComplete && willComplete;
}

/**
 * 播報 Slot Number 語音
 */
async function speakslotNumber(slotNumber, willBeComplete, locale, t) {
  if (!("speechSynthesis" in window)) return;

  const speechKey = willBeComplete
    ? "operator.operations.distribute.speech.order_complete"
    : "operator.operations.distribute.speech.slot_number";

  const text = t(speechKey, { slotNumber });
  const lang = { zh: "zh-TW", en: "en-GB" }[locale] || locale;

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = lang;
  utterance.rate = 1.2;
  utterance.pitch = 1;
  utterance.volume = 1;

  window.speechSynthesis.speak(utterance);
}

/**
 * 更新 Cell 的配貨數據
 */
function updateCellWithDistribution(cell, orderItemId, operator, scannedAt) {
  const itemIndex = cell.distributed_items.findIndex(
    (item) => item.order_item_id === orderItemId,
  );

  if (itemIndex !== -1) {
    const item = { ...cell.distributed_items[itemIndex] };

    if (item.distributed_quantity < item.required_quantity) {
      item.distributed_quantity += 1;
      item.last_distributed_at = scannedAt;
      item.last_distributed_by = operator;
    }

    cell.distributed_items = [...cell.distributed_items];
    cell.distributed_items[itemIndex] = item;
  } else {
    console.warn("[DistributeContext] Item not found!");
  }

  return cell;
}

/**
 * 從 wave.fulfillments 建立初始 distribute state
 */
function buildInitialState(fulfillments) {
  return (fulfillments || [])
    .filter((f) => f.slot_number)
    .map((fulfillment) => ({
      slotNumber: fulfillment.slot_number,
      order: fulfillment.order
        ? {
            id: fulfillment.order.id,
            name: fulfillment.order.name,
            receiver_name: fulfillment.order.receiver_name,
          }
        : { id: fulfillment.id, name: "-", receiver_name: "-" },
      distributed_items: (fulfillment.items || []).map((item) => ({
        order_item_id: item.order_item_id,
        product_id: item.product_id,
        sku: item.sku,
        product_name: item.product_name,
        batch: item.batch,
        barcode: item.barcode,
        required_quantity: item.quantity,
        distributed_quantity: 0,
        last_distributed_at: null,
        last_distributed_by: null,
      })),
    }));
}

export function DistributeProvider({ children }) {
  const { t, i18n } = useTranslation();

  // ==================== State ====================

  // PickingWave 基本資訊
  const [pickingWave, setPickingWave] = useState(null);
  const pickingWaveRef = useRef(null);

  // 配貨狀態（完整狀態）
  const [distributeState, setDistributeState] = useState([]);

  // 異常品（異常區）
  const [virtualOrders, setVirtualOrders] = useState([]);
  const virtualOrdersRef = useRef([]);

  // Grid 設定
  const [gridRows, setGridRows] = useState(5);
  const [gridCols, setGridCols] = useState(5);

  // Loading 和 Error
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 高亮的 Slot Number
  const [highlightedslotNumber, setHighlightedslotNumber] = useState(null);
  const highlightTimeoutRef = useRef(null);

  // 跳號模式（Jump Mode）
  const [jumpMode, setJumpMode] = useState(false);

  // 異常區模式（Exception Mode）
  const [exceptionMode, setExceptionMode] = useState(false);

  // ActionCable 訂閱
  const subscriptionRef = useRef(null);

  // handleVirtualOrder ref
  const handleVirtualOrderRef = useRef(null);

  // 同步 refs
  useEffect(() => {
    pickingWaveRef.current = pickingWave;
  }, [pickingWave]);
  useEffect(() => {
    virtualOrdersRef.current = virtualOrders;
  }, [virtualOrders]);

  // ==================== ActionCable 訂閱 ====================

  const subscribeToWave = useCallback((pickingWaveId) => {
    if (!pickingWaveId) {
      console.warn(
        "[DistributeContext] Cannot subscribe without picking wave ID",
      );
      return;
    }

    // 取消舊的訂閱
    if (subscriptionRef.current) {
      subscriptionRef.current();
      subscriptionRef.current = null;
    }

    // 建立新的訂閱（使用 picking_wave_id）
    const unsubscribe = operatorCable.subscribe(
      "DistributeChannel",
      { picking_wave_id: pickingWaveId },
      {
        connected: () => {},
        disconnected: () => {},
        received: (data) => {
          handleBroadcastReceived(data);
        },
        rejected: () => {
          console.error("[DistributeContext] Subscription rejected");
        },
      },
    );

    subscriptionRef.current = unsubscribe;
  }, []);

  const unsubscribeFromWave = useCallback(() => {
    if (subscriptionRef.current) {
      subscriptionRef.current();
      subscriptionRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      unsubscribeFromWave();
    };
  }, [unsubscribeFromWave]);

  // ==================== 處理接收的廣播訊息 ====================

  const applyDistributionEvent = useCallback((event) => {
    const { slot_number, order_item_id, operator, scanned_at } = event;

    setHighlightedslotNumber(slot_number);

    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedslotNumber(null);
    }, 2000);

    setDistributeState((prevState) => {
      const newState = [...prevState];
      const cellIndex = newState.findIndex(
        (cell) => cell.slotNumber === slot_number,
      );

      if (cellIndex !== -1) {
        const cell = { ...newState[cellIndex] };
        const updatedCell = updateCellWithDistribution(
          cell,
          order_item_id,
          operator,
          scanned_at,
        );
        newState[cellIndex] = updatedCell;
      }

      return newState;
    });
  }, []);

  const applyEventToState = useCallback((state, event) => {
    const { slot_number, order_item_id, operator, scanned_at } = event;
    const newState = [...state];
    const cellIndex = newState.findIndex(
      (cell) => cell.slotNumber === slot_number,
    );

    if (cellIndex !== -1) {
      const cell = { ...newState[cellIndex] };
      const itemIndex = cell.distributed_items.findIndex(
        (item) => item.order_item_id === order_item_id,
      );

      if (itemIndex !== -1) {
        const item = { ...cell.distributed_items[itemIndex] };
        if (item.distributed_quantity < item.required_quantity) {
          item.distributed_quantity += 1;
          item.last_distributed_at = scanned_at;
          item.last_distributed_by = operator;
        }
        cell.distributed_items = [...cell.distributed_items];
        cell.distributed_items[itemIndex] = item;
        newState[cellIndex] = cell;
      }
    }

    return newState;
  }, []);

  const handleDistributionEvent = useCallback(
    (payload) => {
      const currentWave = pickingWaveRef.current;
      if (!currentWave) {
        console.warn(
          "[DistributeContext] Cannot process event: no wave loaded",
        );
        return;
      }
      applyDistributionEvent(payload);
    },
    [applyDistributionEvent],
  );

  const handleHistoryEvents = useCallback(
    (payload) => {
      const { events } = payload;
      const currentWave = pickingWaveRef.current;

      if (!currentWave) {
        console.warn(
          "[DistributeContext] Cannot apply history: no wave loaded",
        );
        return;
      }

      const initial = buildInitialState(currentWave.fulfillments);

      let rebuiltState = initial;
      events.forEach((event) => {
        rebuiltState = applyEventToState(rebuiltState, event);
      });

      setDistributeState(rebuiltState);
    },
    [applyEventToState],
  );

  const handleVirtualOrderEvent = useCallback((payload) => {
    const {
      slot_number,
      code,
      quantity,
      product_data,
      operator,
      created_at,
      last_scanned_at,
    } = payload;

    setVirtualOrders((prevVirtual) => {
      const existing = prevVirtual.find((v) => v.slotNumber === slot_number);

      if (existing) {
        return prevVirtual.map((v) =>
          v.slotNumber === slot_number
            ? { ...v, quantity, last_scanned_at }
            : v,
        );
      }
      return [
        ...prevVirtual,
        {
          slotNumber: slot_number,
          code,
          quantity,
          created_at,
          last_scanned_at,
          isVirtual: true,
          product: product_data,
        },
      ];
    });

    setHighlightedslotNumber(slot_number);
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedslotNumber(null);
    }, 2000);
  }, []);

  const handleEventsClearedBroadcast = useCallback(() => {
    const currentWave = pickingWaveRef.current;
    if (!currentWave) return;

    setDistributeState(buildInitialState(currentWave.fulfillments));
  }, []);

  const handleExceptionModeStatus = useCallback((payload) => {
    setExceptionMode(payload.enabled);
  }, []);

  const handleExceptionModeChanged = useCallback((payload) => {
    setExceptionMode(payload.enabled);
  }, []);

  const handleBroadcastReceived = useCallback(
    (data) => {
      const { type, ...payload } = data;

      switch (type) {
        case "distribution_event":
          handleDistributionEvent(payload);
          break;
        case "virtual_order_event":
          handleVirtualOrderEvent(payload);
          break;
        case "exception_mode_status":
          handleExceptionModeStatus(payload);
          break;
        case "exception_mode_changed":
          handleExceptionModeChanged(payload);
          break;
        case "history_events":
          handleHistoryEvents(payload);
          break;
        case "events_cleared":
          handleEventsClearedBroadcast();
          break;
        default:
          console.warn("[DistributeContext] Unknown broadcast type:", type);
      }
    },
    [
      handleDistributionEvent,
      handleVirtualOrderEvent,
      handleExceptionModeStatus,
      handleExceptionModeChanged,
      handleHistoryEvents,
      handleEventsClearedBroadcast,
    ],
  );

  // ==================== 商品匹配邏輯 ====================

  // matchProductInFulfillments 已提取至 @/utils/distributeMatching.js

  // ==================== API 操作 ====================

  /**
   * 解析條碼並載入 Wave
   *
   * 工作流程：
   * 1. 發送 code 到 resolve 端點（支援 PW/PL/FL/UUID）
   * 2. 從回傳的 wave 資料初始化配貨狀態
   * 3. 訂閱 ActionCable（會收到歷史事件並重建狀態）
   */
  const resolveAndLoadWave = useCallback(
    async (code) => {
      try {
        setLoading(true);
        setError(null);

        const response = await operatorApiFetch("picking_waves/resolve", {
          method: "POST",
          body: JSON.stringify({ code }),
        });
        const data = await response.json();

        if (data.success) {
          const wave = data.data;
          setPickingWave(wave);

          // 初始化配貨狀態
          setDistributeState(buildInitialState(wave.fulfillments));

          // 訂閱 ActionCable（會收到歷史事件並重建狀態）
          subscribeToWave(wave.id);

          return { success: true };
        }
        setError(data.message || "Failed to resolve barcode");
        return { success: false, message: data.message };
      } catch (err) {
        console.error("[DistributeContext] Error resolving barcode:", err);
        setError(err.message);
        return { success: false, message: err.message };
      } finally {
        setLoading(false);
      }
    },
    [subscribeToWave],
  );

  /**
   * 掃描條碼進行配貨
   */
  const scanBarcode = useCallback(
    async (code, exceptionModeEnabled = false) => {
      if (!pickingWave) {
        return { success: false, message: "No wave loaded" };
      }

      try {
        // 前端先匹配商品（傳入 distributeState 以跳過已配滿的 slot）
        const matched = matchProductInFulfillments(
          code,
          pickingWave.fulfillments,
          distributeState,
        );

        if (!matched) {
          if (!exceptionModeEnabled) {
            const errorMessage = t(
              "operator.operations.distribute.errors.no_match",
              {
                defaultValue: "找不到對應的商品",
              },
            );
            setError(errorMessage);
            return { success: false, message: errorMessage };
          }
          if (handleVirtualOrderRef.current) {
            handleVirtualOrderRef.current(code);
          }
          return {
            success: true,
            virtual: true,
            message: t(
              "operator.operations.distribute.exception_zone.item_added",
              {
                defaultValue: "商品已加入異常區",
              },
            ),
          };
        }

        // 播放語音
        const willBeComplete = checkWillBeComplete(
          distributeState,
          matched.slotNumber,
          matched.order_item_id,
        );
        speakslotNumber(matched.slotNumber, willBeComplete, i18n.language, t);

        // 發送匹配結果到後端（使用 wave endpoint）
        const response = await operatorApiFetch(
          `picking_waves/${pickingWave.id}/scan_barcode`,
          {
            method: "POST",
            body: {
              slot_number: matched.slotNumber,
              order_item_id: matched.order_item_id,
              scanned_code: code,
              matched_by: matched.matched_by,
            },
          },
        );

        const data = await response.json();

        if (data.success) {
          return { success: true, ...data.data };
        }
        console.error(
          "[DistributeContext] Failed to send distribution event:",
          data.message,
        );
        setError(data.message);
        return { success: false, message: data.message };
      } catch (err) {
        console.error("[DistributeContext] Error scanning barcode:", err);
        setError(err.message);
        return { success: false, message: err.message };
      }
    },
    [
      pickingWave,
      matchProductInFulfillments,
      distributeState,
      i18n.language,
      t,
    ],
  );

  /**
   * 處理異常品（異常區商品）
   */
  const handleVirtualOrder = useCallback(
    async (code) => {
      try {
        const existing = virtualOrdersRef.current.find((v) => v.code === code);

        if (existing) {
          speakslotNumber(existing.slotNumber, false, i18n.language, t);

          await operatorApiFetch(
            `picking_waves/${pickingWave.id}/scan_barcode`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                is_virtual: true,
                slot_number: existing.slotNumber,
                code,
                quantity: existing.quantity + 1,
                product_data: existing.product,
              }),
            },
          );
        } else {
          const currentState = distributeState || [];
          const currentVirtual = virtualOrdersRef.current || [];
          const usedIndices = [
            ...currentState.map((c) => c.slotNumber),
            ...currentVirtual.map((v) => v.slotNumber),
          ];

          let nextslotNumber = currentState.length + 1;
          while (usedIndices.includes(nextslotNumber)) {
            nextslotNumber++;
          }

          speakslotNumber(nextslotNumber, false, i18n.language, t);

          // 查詢商品資料
          let productData = null;
          try {
            const currentWave = pickingWaveRef.current;
            if (currentWave?.merchant_id) {
              const response = await operatorApiFetch(
                `products?sku_or_barcode=${encodeURIComponent(code)}&merchant_id=${currentWave.merchant_id}`,
              );
              const data = await response.json();

              if (data.success && data.data && data.data.length > 0) {
                const product = data.data[0];
                productData = {
                  id: product.id,
                  name: product.name,
                  sku: product.sku,
                  barcode: product.barcode,
                };
              }
            }
          } catch (err) {
            console.error("[DistributeContext] Error searching product:", err);
          }

          await operatorApiFetch(
            `picking_waves/${pickingWave.id}/scan_barcode`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                is_virtual: true,
                slot_number: nextslotNumber,
                code,
                quantity: 1,
                product_data: productData,
              }),
            },
          );
        }
      } catch (err) {
        console.error("[DistributeContext] Error handling virtual order:", err);
        setError(err.message || "Failed to handle virtual order");
      }
    },
    [distributeState, pickingWave, i18n.language, t],
  );

  useEffect(() => {
    handleVirtualOrderRef.current = handleVirtualOrder;
  }, [handleVirtualOrder]);

  /**
   * 清除配貨事件記錄
   */
  const clearDistributeEvents = useCallback(async () => {
    if (!pickingWave) {
      return { success: false, message: "No wave loaded" };
    }

    try {
      const response = await operatorApiFetch(
        `picking_waves/${pickingWave.id}/clear_distribute_events`,
        { method: "DELETE" },
      );

      const data = await response.json();

      if (data.success) {
        return { success: true, message: data.message };
      }
      setError(data.message);
      return { success: false, message: data.message };
    } catch (err) {
      console.error(
        "[DistributeContext] Error clearing distribute events:",
        err,
      );
      setError(err.message);
      return { success: false, message: err.message };
    }
  }, [pickingWave]);

  /**
   * 切換異常區模式（Exception Mode）
   */
  const toggleExceptionMode = useCallback(
    async (enabled) => {
      if (!pickingWave) {
        setError(
          t("operator.operations.distribute.exception_zone.disabled_tooltip"),
        );
        return false;
      }

      try {
        setExceptionMode(enabled);

        await operatorApiFetch(
          `picking_waves/${pickingWave.id}/toggle_exception_mode`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ enabled }),
          },
        );

        return true;
      } catch (err) {
        console.error(
          "[DistributeContext] Error toggling exception mode:",
          err,
        );
        setExceptionMode(!enabled);
        setError(err.message || "Failed to toggle exception mode");
        return false;
      }
    },
    [pickingWave],
  );

  /**
   * 重置配貨狀態
   */
  const resetDistributeState = useCallback(() => {
    setPickingWave(null);
    setDistributeState([]);
    setError(null);
    unsubscribeFromWave();
  }, [unsubscribeFromWave]);

  // ==================== Context Value ====================

  const value = {
    // State
    pickingWave,
    distributeState,
    virtualOrders,
    gridRows,
    gridCols,
    loading,
    error,
    highlightedslotNumber,
    jumpMode,
    exceptionMode,

    // Actions
    resolveAndLoadWave,
    scanBarcode,
    clearDistributeEvents,
    resetDistributeState,
    setGridRows,
    setGridCols,
    setJumpMode,
    toggleExceptionMode,
    setVirtualOrders,

    // Utilities
    getOrderByslotNumber: useCallback(
      (slotNumber) => {
        return distributeState.find((cell) => cell.slotNumber === slotNumber);
      },
      [distributeState],
    ),

    getVirtualOrderByslotNumber: useCallback(
      (slotNumber) => {
        return virtualOrders.find((v) => v.slotNumber === slotNumber);
      },
      [virtualOrders],
    ),

    getTotalDistributedForOrder: useCallback(
      (slotNumber) => {
        const cell = distributeState.find((c) => c.slotNumber === slotNumber);
        if (!cell) return 0;

        return cell.distributed_items.reduce(
          (sum, item) => sum + (item.distributed_quantity || 0),
          0,
        );
      },
      [distributeState],
    ),

    isOrderComplete: useCallback(
      (slotNumber) => {
        const cell = distributeState.find((c) => c.slotNumber === slotNumber);
        if (!cell) return false;

        return cell.distributed_items.every(
          (item) => item.distributed_quantity >= item.required_quantity,
        );
      },
      [distributeState],
    ),
  };

  return (
    <DistributeContext.Provider value={value}>
      {children}
    </DistributeContext.Provider>
  );
}
